import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { TimeclockReportDetail, TimeclockReportsSummary } from '../types';
import PremiumSelect from './PremiumSelect';
import StatusChip, { StatusTone } from './ui/StatusChip';
import { formatTimeclockEntryKind } from '../utils/timeclockEntryKind';
import { downloadBlob, filenameFromContentDisposition } from '../utils/downloadBlob';

function formatMinutes(m: number): string {
  const sign = m < 0 ? '−' : '';
  const a = Math.abs(m);
  return `${sign}${Math.floor(a / 60)}h ${a % 60}min`;
}

/** Maps a balance (in minutes) to a StatusChip tone / text color intent. */
function balanceTone(minutes: number): StatusTone {
  if (minutes < 0) return 'danger';
  if (minutes > 0) return 'success';
  return 'neutral';
}

const BALANCE_TEXT_COLOR: Record<StatusTone, string> = {
  danger: 'error.main',
  success: 'success.main',
  neutral: 'text.primary',
  accent: 'primary.main',
  warning: 'warning.main',
  info: 'info.main',
};

const BalanceText: React.FC<{ minutes: number }> = ({ minutes }) => (
  <Box
    component="span"
    sx={{ color: BALANCE_TEXT_COLOR[balanceTone(minutes)], fontWeight: 700 }}
  >
    {formatMinutes(minutes)}
  </Box>
);

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateBr(iso: string): string {
  const [y, mo, d] = iso.slice(0, 10).split('-');
  return `${d}/${mo}/${y}`;
}

function formatYmdDashToBr(ymd: string): string {
  const [y, mo, d] = ymd.split('-');
  if (!y || !mo || !d) return ymd;
  return `${d}/${mo}/${y}`;
}

function isoToBrazilDateAndTime(iso: string): { workDate: string; timeHM: string } {
  const d = new Date(iso);
  const workDate = d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const hh = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const mm = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return { workDate, timeHM: `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}` };
}

const MONTH_SELECT_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1).padStart(2, '0'),
}));

const TimeclockReportsPanel: React.FC = () => {
  const { user } = useContext(AuthContext) || {};
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [msg, setMsg] = useState<string | null>(null);
  const [reportsSummary, setReportsSummary] = useState<TimeclockReportsSummary | null>(null);
  const [reportsDetail, setReportsDetail] = useState<TimeclockReportDetail | null>(null);
  const [reportsUserId, setReportsUserId] = useState('');
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsExporting, setReportsExporting] = useState(false);
  const reportsIndividualRef = useRef<HTMLDivElement>(null);

  const loadReportsSummary = useCallback(async (): Promise<void> => {
    setReportsLoading(true);
    try {
      const res = await api.get<TimeclockReportsSummary>(
        `/timeclock/team/reports/summary?year=${year}&month=${month}`,
      );
      setReportsSummary(res.data);
      setReportsUserId((prev) => {
        if (prev && res.data.collaborators.some((c) => c.userId === prev)) return prev;
        return res.data.collaborators[0]?.userId ?? '';
      });
    } catch {
      setReportsSummary(null);
      setReportsUserId('');
      setMsg('Erro ao carregar relatórios de ponto.');
    } finally {
      setReportsLoading(false);
    }
  }, [year, month]);

  const loadReportsDetail = useCallback(
    async (userId: string): Promise<void> => {
      if (!userId) return;
      try {
        const res = await api.get<TimeclockReportDetail>(
          `/timeclock/team/reports/detail?userId=${userId}&year=${year}&month=${month}`,
        );
        setReportsDetail(res.data);
      } catch {
        setReportsDetail(null);
        setMsg('Erro ao carregar detalhe do relatório.');
      }
    },
    [year, month],
  );

  useEffect(() => {
    void loadReportsSummary();
  }, [loadReportsSummary]);

  useEffect(() => {
    if (!reportsUserId) {
      setReportsDetail(null);
      return;
    }
    void loadReportsDetail(reportsUserId);
  }, [reportsUserId, loadReportsDetail]);

  const reportsMemberOptions = useMemo(
    () =>
      (reportsSummary?.collaborators ?? []).map((c) => ({
        value: c.userId,
        label: c.name,
      })),
    [reportsSummary],
  );

  const reportsEntriesSorted = useMemo(
    () =>
      [...(reportsDetail?.entries ?? [])].sort(
        (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
      ),
    [reportsDetail],
  );

  const exportTimeclockReport = async (userId: string): Promise<void> => {
    if (!userId) return;
    setReportsExporting(true);
    try {
      const res = await api.get(
        `/timeclock/team/reports/export?userId=${userId}&year=${year}&month=${month}`,
        { responseType: 'blob' },
      );
      const name =
        filenameFromContentDisposition(
          (res.headers as Record<string, string>)['content-disposition'],
        ) ?? `ponto-${userId}.xlsx`;
      downloadBlob(res.data, name);
      setMsg('Relatório exportado.');
    } catch {
      setMsg('Erro ao exportar relatório individual.');
    } finally {
      setReportsExporting(false);
    }
  };

  const openReportsIndividual = useCallback((userId: string): void => {
    setReportsUserId(userId);
    window.setTimeout(() => {
      reportsIndividualRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }, []);

  const exportTimeclockReportAll = async (): Promise<void> => {
    setReportsExporting(true);
    try {
      const res = await api.get(`/timeclock/team/reports/export-all?year=${year}&month=${month}`, {
        responseType: 'blob',
      });
      const name =
        filenameFromContentDisposition(
          (res.headers as Record<string, string>)['content-disposition'],
        ) ?? `ponto-relatorio-geral-${month}-${year}.xlsx`;
      downloadBlob(res.data, name);
      setMsg('Relatório geral exportado.');
    } catch {
      setMsg('Erro ao exportar relatório geral.');
    } finally {
      setReportsExporting(false);
    }
  };

  return (
    <Box>
      {msg ? (
        <Alert severity="info" role="status" sx={{ mb: 2 }}>
          {msg}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2, alignItems: 'flex-end' }}>
        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
          <PremiumSelect
            id="reports-tc-month"
            label="Mês"
            value={String(month)}
            options={MONTH_SELECT_OPTIONS}
            onChange={(v) => setMonth(Number(v))}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
          <TextField
            label="Ano"
            type="number"
            fullWidth
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            slotProps={{ htmlInput: { min: 2020, max: 2040 } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 8 }}>
          <Stack direction="row" spacing={1} sx={{ justifyContent: { sm: 'flex-end' } }}>
            <Button
              type="button"
              variant="outlined"
              disabled={reportsExporting || reportsLoading || !reportsSummary?.collaborators.length}
              onClick={() => void exportTimeclockReportAll()}
            >
              {reportsExporting ? 'Exportando…' : 'Exportar todos (Excel)'}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {user?.role === 'manager'
          ? 'Colaboradores sob sua responsabilidade no período selecionado.'
          : 'Todos os colaboradores ativos no período selecionado.'}
      </Typography>

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Visão geral — banco de horas
      </Typography>
      {reportsLoading ? (
        <Typography color="text.secondary">Carregando relatórios…</Typography>
      ) : !reportsSummary?.collaborators.length ? (
        <Typography color="text.secondary">Nenhum colaborador no seu escopo de relatório.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Colaborador</TableCell>
                <TableCell>Saldo banco</TableCell>
                <TableCell>Saldo mês</TableCell>
                <TableCell>Batidas</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportsSummary.collaborators.map((c) => (
                <TableRow key={c.userId}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>
                    <BalanceText minutes={c.balanceMinutes} />
                  </TableCell>
                  <TableCell>
                    <BalanceText minutes={c.monthBalanceMinutes} />
                  </TableCell>
                  <TableCell>{c.entriesCount}</TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}
                    >
                      <Button
                        type="button"
                        variant="contained"
                        size="small"
                        onClick={() => openReportsIndividual(c.userId)}
                      >
                        Ver detalhe
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        disabled={reportsExporting}
                        onClick={() => void exportTimeclockReport(c.userId)}
                      >
                        Excel
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box ref={reportsIndividualRef} id="tc-reports-individual" sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Relatório individual
        </Typography>
        <Box sx={{ maxWidth: 360, mb: 2 }}>
          <PremiumSelect
            id="reports-tc-member"
            label="Colaborador"
            value={reportsUserId}
            options={reportsMemberOptions}
            onChange={setReportsUserId}
          />
        </Box>
        {reportsDetail ? (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Saldo banco de horas
                  </Typography>
                  <Typography variant="h6" component="div">
                    <BalanceText minutes={reportsDetail.balanceMinutes} />
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Saldo do mês
                  </Typography>
                  <Typography variant="h6" component="div">
                    <BalanceText minutes={reportsDetail.monthBalanceMinutes} />
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                type="button"
                variant="contained"
                disabled={reportsExporting || !reportsUserId}
                onClick={() => void exportTimeclockReport(reportsUserId)}
              >
                {reportsExporting ? 'Exportando…' : 'Exportar Excel (individual)'}
              </Button>
            </Stack>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Resumo por dia
            </Typography>
            <TableContainer component={Paper} sx={{ overflowX: 'auto', mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Entrada</TableCell>
                    <TableCell>Saída almoço</TableCell>
                    <TableCell>Volta almoço</TableCell>
                    <TableCell>Saída</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Saldo dia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportsDetail.days.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{formatDateBr(d.workDate)}</TableCell>
                      <TableCell>{formatTime(d.firstCheckInAt)}</TableCell>
                      <TableCell>{formatTime(d.lunchOutAt)}</TableCell>
                      <TableCell>{formatTime(d.lunchInAt)}</TableCell>
                      <TableCell>{formatTime(d.lastCheckOutAt)}</TableCell>
                      <TableCell>
                        <StatusChip
                          label={d.statusLabel || '—'}
                          tone={balanceTone(d.dailyBalanceMinutes)}
                        />
                      </TableCell>
                      <TableCell>
                        <BalanceText minutes={d.dailyBalanceMinutes} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Log de entradas e saídas
            </Typography>
            {reportsEntriesSorted.length === 0 ? (
              <Typography color="text.secondary">Nenhuma batida neste mês.</Typography>
            ) : (
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Hora</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Registrado em</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportsEntriesSorted.map((en) => {
                      const br = isoToBrazilDateAndTime(en.occurredAt);
                      return (
                        <TableRow key={en.id}>
                          <TableCell>{formatYmdDashToBr(br.workDate)}</TableCell>
                          <TableCell>{br.timeHM}</TableCell>
                          <TableCell>{formatTimeclockEntryKind(en.kind)}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {new Date(en.createdAt).toLocaleString('pt-BR', {
                              timeZone: 'America/Sao_Paulo',
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : reportsUserId && !reportsLoading ? (
          <Typography color="text.secondary">Carregando detalhe do colaborador…</Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default TimeclockReportsPanel;
